import { useEffect, useRef } from "react";
import { usePlayerStore } from "../player/playerStore.js";
import { useProgressStore } from "./progressStore.js";
import { effectiveDuration } from "../player/duration.js";

/**
 * Persist watch progress for VOD/episode playback (Sections 9-10). Watches the
 * shared player store: while playingContent is set, throttled saves every 5s
 * while playing and an immediate save when playback pauses/ends.
 */
export function useSaveProgress(): void {
  const playingContent = usePlayerStore((s) => s.playingContent);
  const status = usePlayerStore((s) => s.status);
  const currentTime = usePlayerStore((s) => s.currentTime);
  const duration = usePlayerStore((s) => s.duration);
  const source = usePlayerStore((s) => s.source);
  const lastRef = useRef<{ ref: string; at: number }>({ ref: "", at: 0 });

  useEffect(() => {
    if (!playingContent || status === "idle") return;
    const save = () => {
      if (currentTime <= 0) return;
      // The same rule the seek bar uses (player/duration.ts): a transcode's
      // growing frontier or a mis-measured catch-up replay must not be saved as
      // the length, or Continue Watching's "% watched" is wrong until the next
      // save overwrites it.
      const savedDuration = effectiveDuration(source, duration);
      useProgressStore.getState().save({
        contentType: playingContent.contentType,
        contentRef: playingContent.contentRef,
        positionSeconds: currentTime,
        durationSeconds: savedDuration,
        ...(playingContent.profileId ? { profileId: playingContent.profileId } : {}),
      });
    };
    if (status !== "playing") {
      // Paused / ended / error: save now.
      save();
    } else if (lastRef.current.ref !== playingContent.contentRef || Date.now() - lastRef.current.at >= 5000) {
      save();
      lastRef.current = { ref: playingContent.contentRef, at: Date.now() };
    }
  }, [playingContent, status, currentTime, duration, source]);
}
